import { fabric } from 'fabric';

export interface LineCoordinates {
  topline: {
    o: fabric.Point;
    d: fabric.Point;
  };
  rightline: {
    o: fabric.Point;
    d: fabric.Point;
  };
  bottomline: {
    o: fabric.Point;
    d: fabric.Point;
  };
  leftline: {
    o: fabric.Point;
    d: fabric.Point;
  };
}

export enum ObjectType {
  Artboard = 'artboard',
  Illustration = 'illustration',
  IllustrationImage = 'illustrationImage',
  BasicShape = 'basicShape',
  CombinedShape = 'combinedShape',
  PathText = 'pathText',
  OverlayTexture = 'overlayTexture',
  ActiveSelection = 'activeSelection',
  WarpedImage = 'warpedImage',
  Mask = 'mask',
  ArtboardTitle = 'artboardTitle',
  ArtboardTitleIcon = 'artboardTitleIcon',
  Line = 'line',
  CustomTextbox = 'CustomTextbox',
  ClipPath = 'clipPath',
  Path = 'path',
  Circle = 'circle',
  Rect = 'rect',
  RichText = 'RichText',
  EditablePolygon = 'editablePolygon',
  Ruler = 'ruler',
  Guide = 'guide',
  WarpControlRootPoint = 'warpControlRootPoint',
  Group = 'group',
}

export enum Orientation {
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical',
}

export enum CanvasEventName {
  // Fabric built-in events
  ObjectModified = 'object:modified',
  ObjectMoving = 'object:moving',
  ObjectScaling = 'object:scaling',
  ObjectRotating = 'object:rotating',
  ObjectSkewing = 'object:skewing',

  // Custom events
  GroupsAdded = 'groups:added',
  GroupsRemoved = 'groups:removed',

  /**
   * Custom outpainting events.
   *
   * @see startOutpainting
   * @see updateOutpaintAreaControlPoints
   * @see exitOutpainting
   */
  OutpaintingStarted = 'outpainting:started',
  OutpaintingAreaResized = 'outpainting:areaResized',
  OutpaintingExited = 'outpainting:exited',
}

/**
 * Event payload when an object is dragged, rotated, skewed, scaled
 */
export type ObjectTransformedPayload = {
  target: fabric.Object;
};

/**
 * Event payload for 'groups:added' and 'groups:removed'
 */
export type GroupsAddedRemovedPayload = {
  groupIds: string[];
};

export enum ObjectModifiedEventAction {
  // Fabric built-in transform actions
  Move = 'drag',
  Scale = 'scale',
  ScaleX = 'scaleX',
  ScaleY = 'scaleY',
  Rotate = 'rotate',
  Skew = 'skew',
  FlipX = 'flipX',
  FlipY = 'flipY',

  // Custom actions
  LoadState = 'loadState',
  ActiveGroup = 'active:group',
}

type ObjectsChangedPayload = {
  target: fabric.Object;
  action:
    | ObjectModifiedEventAction.ActiveGroup
    | ObjectModifiedEventAction.Move
    | ObjectModifiedEventAction.Scale
    | ObjectModifiedEventAction.ScaleX
    | ObjectModifiedEventAction.ScaleY
    | ObjectModifiedEventAction.Rotate
    | ObjectModifiedEventAction.Skew
    | ObjectModifiedEventAction.FlipX
    | ObjectModifiedEventAction.FlipY;
};

type BoardLoadedPayload = {
  action: ObjectModifiedEventAction.LoadState;
};

export type ObjectModifiedEvent = (
  | ObjectsChangedPayload
  | BoardLoadedPayload
  | {
      target: fabric.Object;
    }
  | {
      targets: fabric.Object[];
    }
) & {
  /**
   * Avoid that the event is tracked with the undo/redo history.
   * Useful to sync a change with the yjs document without adding
   * an extra step to the undo/redo history.
   */
  skipHistory?: boolean;
  /**
   * Log debugging information from the collab system, including the exact
   * changes that will be committed to history
   */
  debug?: boolean;
};

export type OutpaintingAreaResizedEvent = {
  width: number;
  height: number;
};

export type SerializableObjectType =
  | ObjectType.IllustrationImage
  | ObjectType.Illustration
  | ObjectType.Artboard
  | ObjectType.PathText
  | ObjectType.OverlayTexture
  | ObjectType.Mask
  | ObjectType.BasicShape
  | ObjectType.Guide
  | ObjectType.CombinedShape;

export enum ScaleControlCorner {
  'tl' = 'tl',
  'tr' = 'tr',
  'bl' = 'bl',
  'br' = 'br',
}

export enum CropControlCorner {
  'ctl' = 'ctl',
  'ctr' = 'ctr',
  'cbl' = 'cbl',
  'cbr' = 'cbr',
  'cmt' = 'cmt',
  'cmb' = 'cmb',
  'cml' = 'cml',
  'cmr' = 'cmr',
}

// TODO: Replace CropControlCorner everywhere with this.
//   Consider also renaming variants cxx to axx.
export type AreaControlCorner = CropControlCorner;
export const AreaControlCorner = CropControlCorner;

export enum ObjectOriginX {
  Left = 'left',
  Right = 'right',
  Center = 'center',
}

export enum ObjectOriginY {
  Top = 'top',
  Bottom = 'bottom',
  Center = 'center',
}

export type ToSVGReviver = (markup: string) => string;

export interface Dimensions {
  width: number;
  height: number;
}

export interface Bounds {
  left: number;
  top: number;
  width: number;
  height: number;
}

export type FabricPathCommand =
  | ['h' | 'v', number]
  | ['L' | 'l' | 'H' | 'V' | 't' | 'M' | 'm', number, number]
  | ['q' | 's', number, number, number, number]
  | ['c', number, number, number, number, number, number]
  | ['a', number, number, number, number, number, number, number]
  | ['z' | 'Z'];

export type FabricPathCommands = FabricPathCommand[];
